import { useMemo } from 'react'
import { Flex } from '@npco/zeller-design-system'
import dayjs from 'dayjs'

import { useSelectedAccountId } from '../hooks/useSelectedAccount/useSelectedAccountId'
import { useSelectedDateRange } from '../hooks/useSelectedDateRange'
import { InverseIncomeOrExpenseMessage } from '../InverseIncomeOrExpenseMessage'
import { useChartDateRange } from './hooks/useChartDateRange/useChartDateRange'
import { useTotalIncomeAndExpenseData } from './hooks/useTotalIncomeAndExpense/useTotalIncomeAndExpenseData'
import { IncomeAndExpenseChart } from './IncomeAndExpenseChart/IncomeAndExpenseChart'
import { IncomeAndExpenseDatapoints } from './IncomeAndExpenseDatapoints/IncomeAndExpenseDatapoints'
import { IncomeAndExpenseError } from './IncomeAndExpenseError/IncomeAndExpenseError'

type TotalIncomeAndExpenseProps = {
  entityTimezone: string | undefined
  isLoadingTimezone: boolean
}

export const TotalIncomeAndExpense = ({
  entityTimezone,
  isLoadingTimezone,
}: TotalIncomeAndExpenseProps) => {
  const selectedAccount = useSelectedAccountId()
  const { selectedDateRange } = useSelectedDateRange()

  const {
    chartDate,
    goToPreviousMonth,
    goToNextMonth,
    hasInitialisedTimezone,
  } = useChartDateRange({
    entityTimezone,
    isLoadingTimezone,
  })

  const {
    incomeAndExpenses,
    isLoadingIncomeAndExpenses,
    error,
    refetch,
    isRefetching,
    isUpdatingVariables,
  } = useTotalIncomeAndExpenseData({
    accountUuid:
      selectedAccount === 'all accounts'
        ? undefined
        : selectedAccount.accountId,
    date: chartDate,
    timeZone: entityTimezone,
    skip: !hasInitialisedTimezone,
  })

  const hasInverseExpenseOrIncome = useMemo(() => {
    return incomeAndExpenses.some((incomeAndExpense) => {
      const isIncomeNegative =
        parseInt(
          incomeAndExpense.transactionDirectionTotalAmounts.income.value,
          10
        ) < 0
      const isExpenseNegative =
        parseInt(
          incomeAndExpense.transactionDirectionTotalAmounts.expense.value,
          10
        ) < 0
      return isIncomeNegative || isExpenseNegative
    })
  }, [incomeAndExpenses])

  const isLoading =
    (isLoadingIncomeAndExpenses && incomeAndExpenses.length === 0) ||
    isRefetching ||
    isLoadingTimezone ||
    !hasInitialisedTimezone

  const isFetchingMore = isUpdatingVariables && incomeAndExpenses.length === 0

  const shouldDisableNextButton = !dayjs(chartDate)
    .endOf('month')
    .isBefore(dayjs().tz(entityTimezone))

  return (
    <Flex
      flexDirection="column"
      gap="16px"
      aria-label="Account balance over time"
      as="section"
      mb="34px"
    >
      <IncomeAndExpenseDatapoints
        incomeAndExpenses={incomeAndExpenses}
        isLoadingIncomeAndExpenses={isLoading}
        selectedDateRange={selectedDateRange}
        timezone={entityTimezone}
      />
      {error ? (
        <IncomeAndExpenseError handleTryAgain={refetch} />
      ) : (
        <>
          <IncomeAndExpenseChart
            isLoadingIncomeAndExpenses={isLoading}
            incomeAndExpenses={incomeAndExpenses}
            goToPreviousMonth={goToPreviousMonth}
            goToNextMonth={goToNextMonth}
            isFetchingMore={isFetchingMore}
            shouldDisableNextButton={shouldDisableNextButton}
            timezone={entityTimezone}
          />
          {!isLoading && hasInverseExpenseOrIncome && (
            <InverseIncomeOrExpenseMessage />
          )}
        </>
      )}
    </Flex>
  )
}
